import type { VgGraph } from '../schema.js';

/**
 * Deterministic Markdown summary (`GRAPH_REPORT.md`). Given a pinned
 * `generatedAt`, the report is byte-stable. Numbers are derived purely from the
 * graph, so the report can be regenerated from a committed `graph.json`.
 */
export function renderReport(graph: VgGraph): string {
  const { meta, provenance, nodes, edges } = graph;
  const lines: string[] = [];

  lines.push('# Code Graph Report');
  lines.push('');
  lines.push(`Generated by \`vg\` ${provenance.version} · schema \`${graph.schemaVersion}\``);
  lines.push('');
  lines.push('## Summary');
  lines.push('');
  lines.push(`- **Nodes:** ${meta.counts.nodes.toLocaleString('en-US')}`);
  lines.push(`- **Edges:** ${meta.counts.edges.toLocaleString('en-US')}`);
  lines.push(`- **Areas:** ${meta.counts.areas.toLocaleString('en-US')}`);
  lines.push(`- **Languages:** ${meta.languages.join(', ') || '—'}`);
  lines.push(`- **Clustering:** ${meta.cluster}`);
  lines.push(`- **Resolvers:** ${provenance.resolver.join(', ')}`);
  lines.push(`- **Edge kinds:** ${meta.edgeKinds.join(', ') || '—'}`);
  lines.push('');

  // Files by definition count.
  const fileNodes = nodes.filter((n) => n.kind === 'file');
  const defsByFile = new Map<string, number>();
  for (const n of nodes) {
    if (n.kind === 'file' || n.kind === 'external') continue;
    defsByFile.set(n.file, (defsByFile.get(n.file) ?? 0) + 1);
  }
  const topFiles = [...defsByFile.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 15);
  if (topFiles.length) {
    lines.push('## Largest files (by definitions)');
    lines.push('');
    lines.push('| File | Definitions |');
    lines.push('| --- | ---: |');
    for (const [file, count] of topFiles) lines.push(`| \`${file}\` | ${count} |`);
    lines.push('');
  }

  // Most-connected nodes (Phase 0 importance = normalised degree).
  const topNodes = nodes
    .filter((n) => n.kind !== 'file' && n.kind !== 'external')
    .sort((a, b) => b.importance - a.importance || a.qualifiedName.localeCompare(b.qualifiedName))
    .slice(0, 15);
  if (topNodes.length) {
    lines.push('## Most-connected definitions');
    lines.push('');
    lines.push('| Symbol | Kind | File | Importance |');
    lines.push('| --- | --- | --- | ---: |');
    for (const n of topNodes) {
      lines.push(
        `| \`${n.qualifiedName}\` | ${n.kind} | \`${n.file}\`:${n.span.start} | ${n.importance.toFixed(3)} |`,
      );
    }
    lines.push('');
  }

  // Edge-kind breakdown.
  const byKind = new Map<string, number>();
  for (const e of edges) byKind.set(e.kind, (byKind.get(e.kind) ?? 0) + 1);
  if (byKind.size) {
    lines.push('## Edges by kind');
    lines.push('');
    lines.push('| Kind | Count |');
    lines.push('| --- | ---: |');
    for (const [kind, count] of [...byKind.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      lines.push(`| ${kind} | ${count} |`);
    }
    lines.push('');
  }

  lines.push('---');
  lines.push('');
  lines.push(`_Files: ${fileNodes.length} · Corpus: \`${provenance.corpusHash.slice(0, 16)}…\`_`);
  lines.push('');

  return lines.join('\n');
}
